
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { navigate } from './navigation-service';
import { supabase } from '@/integrations/supabase/client';

// Define the AppUrlOpen type since it's not exported from @capacitor/app
interface AppUrlOpen {
  url: string;
}

export class DeepLinkService {
  private static instance: DeepLinkService;
  private initialized = false;

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): DeepLinkService {
    if (!DeepLinkService.instance) {
      DeepLinkService.instance = new DeepLinkService();
    }
    return DeepLinkService.instance;
  }

  public init(): void {
    if (this.initialized || !Capacitor.isNativePlatform()) {
      return;
    }

    console.log('DeepLinkService: Initializing deep link handler');
    
    // Listen for deep links
    App.addListener('appUrlOpen', (data: AppUrlOpen) => {
      console.log('DeepLinkService: App opened with URL:', data.url);
      this.handleIncomingUrl(data.url);
    });

    this.initialized = true;
  }

  private handleIncomingUrl(url: string): void {
    console.log('DeepLinkService: Processing URL:', url);
    
    try {
      // Parse the URL
      const parsedUrl = new URL(url);

      // 🔥 Trata manualmente o callback OAuth de apps nativos
if (parsedUrl.host === 'auth' && parsedUrl.pathname === '/callback') {
  console.log('DeepLinkService: OAuth callback detected');

  const hashFragment = url.split('#')[1] || '';
  const query = new URLSearchParams(hashFragment);

  const accessToken = query.get('access_token');
  const refreshToken = query.get('refresh_token');
  const errorDesc = query.get('error_description');

  if (errorDesc) {
    console.error('OAuth error:', errorDesc);
    navigate('/login');
    return;
  }

  if (accessToken && refreshToken) {
    supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
      .then(({ error }) => {
        if (error) {
          console.error('Erro ao setar sessão:', error.message);
          navigate('/login');
        } else {
          console.log('✅ Sessão Supabase restaurada com sucesso');
          navigate('/home');
        }
      });
  } else {
    console.warn('Tokens não encontrados no callback');
    navigate('/login');
  }

  return;
}

      // Extract the path and query parameters
      let path = parsedUrl.pathname;
      const params = Object.fromEntries(parsedUrl.searchParams.entries());
      
      console.log('DeepLinkService: Parsed path:', path);
      console.log('DeepLinkService: Parsed params:', params);
      console.log('DeepLinkService: Parsed host:', parsedUrl.host);
      
      // Handle universal links from go.pulsefiction.app
      if (parsedUrl.host === 'go.pulsefiction.app') {
        // For universal links, we route directly to the path
        console.log('DeepLinkService: Handling universal link');
        
        // Clean up path - remove leading slash if exists, then add it back for consistency
        path = path.startsWith('/') ? path : `/${path}`;
        
        // Handle specific known routes
        if (path.startsWith('/preview/chapter/')) {
          const chapterId = path.replace('/preview/chapter/', '');
          navigate(`/preview/chapter/${chapterId}`, params);
        } else if (path.startsWith('/preview/story/')) {
          const storyId = path.replace('/preview/story/', '');
          navigate(`/preview/story/${storyId}`, params);
        } else if (path.startsWith('/chapter/')) {
          const chapterId = path.replace('/chapter/', '');
          navigate(`/chapter/${chapterId}`, params);
        } else if (path.startsWith('/story/')) {
          const storyId = path.replace('/story/', '');
          navigate(`/ip/${storyId}`, params);
        } else if (path.startsWith('/ip/')) {
          const ipId = path.replace('/ip/', '');
          navigate(`/ip/${ipId}`, params);
        } else if (path.startsWith('/profile/')) {
          const profileId = path.replace('/profile/', '');
          navigate(`/profile/${profileId}`, params);
        } else if (path === '/home' || path === '/') {
          navigate('/home', params);
        } else if (path === '/discover') {
          navigate('/discover', params);
        } else if (path === '/library') {
          navigate('/library', params);
        } else if (path === '/shop') {
          navigate('/shop', params);
        } else if (path === '/settings') {
          navigate('/settings', params);
        } else if (path === '/login' || path === '/signup' || path === '/reset-password' || path === '/enter-password' || path === '/set-password' || path === '/email-intake') {
          navigate(path, params);
        } else {
          // For any other path, try to navigate to it directly
          // This makes it future-proof for new routes
          console.log('DeepLinkService: Attempting to navigate to unknown path:', path);
          navigate(path, params);
        }
      } else {
        // Handle custom scheme links (pulsefiction://)
        console.log('DeepLinkService: Handling custom scheme link');
        
        // For custom schemes, handle as before
        if (path.startsWith('/preview/chapter/')) {
          const chapterId = path.replace('/preview/chapter/', '');
          navigate(`/preview/chapter/${chapterId}`, params);
        } else if (path.startsWith('/preview/story/')) {
          const storyId = path.replace('/preview/story/', '');
          navigate(`/preview/story/${storyId}`, params);
        } else if (path.startsWith('/chapter/')) {
          const chapterId = path.replace('/chapter/', '');
          navigate(`/chapter/${chapterId}`, params);
        } else if (path.startsWith('/story/')) {
          const storyId = path.replace('/story/', '');
          navigate(`/story/${storyId}`, params);
        } else if (path === '/login' || path === '/signup' || path === '/reset-password' || path === '/enter-password' || path === '/set-password' || path === '/email-intake') {
          navigate(path, params);
        } else {
          // Default to home if we don't recognize the path
          navigate('/home', params);
        }
      }
    } catch (error) {
      console.error('DeepLinkService: Error parsing deep link URL:', error);
      // On error, navigate to home as fallback
      navigate('/home', {});
    }
  }
  
  // For testing in web environments
  public simulateDeepLink(url: string): void {
    console.log('DeepLinkService: Simulating deep link with URL:', url);
    this.handleIncomingUrl(url);
  }
}

export default DeepLinkService.getInstance();
